/**
 * PrimitiveLibrary Component
 *
 * Read-only Swagger-style reference for published primitives. Each row expands
 * inline to reveal description, input/output schemas, and metadata.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { FlowDefinition } from '@simcheck/shared-types';
import {
  Layers,
  Tag,
  CheckCircle,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Inbox,
  Loader2,
} from 'lucide-react';
import primitiveService from '../../services/primitiveService';
import SchemaPreview from './SchemaPreview';
import { useDateFormat } from '@/hooks/useDateFormat';
import {
  useDataTableFilters,
  DataTableFilter,
  createColumnConfigHelper,
  applyFilters,
} from '@/components/data-table-filter';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const filterColumnHelper = createColumnConfigHelper<FlowDefinition>();
const filterColumnsConfig = [
  filterColumnHelper
    .text()
    .id('name')
    .accessor((row) => row.name)
    .displayName('Name')
    .icon(Layers)
    .build(),
  filterColumnHelper
    .text()
    .id('description')
    .accessor((row) => row.description || '')
    .displayName('Description')
    .icon(Tag)
    .build(),
  filterColumnHelper
    .option()
    .id('category')
    .accessor((row) => row.category || '')
    .displayName('Category')
    .icon(Tag)
    .options([])
    .build(),
  filterColumnHelper
    .option()
    .id('isPublished')
    .accessor((row) => (row.isPublished ? 'published' : 'draft'))
    .displayName('Status')
    .icon(CheckCircle)
    .options([
      { label: 'Published', value: 'published' },
      { label: 'Draft', value: 'draft' },
    ])
    .build(),
] as const;

const SortIcon: React.FC<{ active: boolean; direction: 'asc' | 'desc' }> = ({ active, direction }) => {
  if (!active) {
    return <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/30" />;
  }
  return direction === 'asc'
    ? <ChevronUp className="w-3.5 h-3.5 text-foreground" />
    : <ChevronDown className="w-3.5 h-3.5 text-foreground" />;
};

export const PrimitiveLibrary: React.FC = () => {
  const fmt = useDateFormat();
  const [primitives, setPrimitives] = useState<FlowDefinition[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<'name' | 'category' | 'version'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: 'name' | 'category' | 'version') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const updatedFilterColumnsConfig = useMemo(() => {
    return filterColumnsConfig.map((col) => {
      if (col.id === 'category' && col.type === 'option') {
        return {
          ...col,
          options: categories.map((cat) => ({ label: cat, value: cat })),
        };
      }
      return col;
    });
  }, [categories]);

  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: primitives,
    columnsConfig: updatedFilterColumnsConfig,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [primitivesData, categoriesData] = await Promise.all([
        primitiveService.getPublishedPrimitives(),
        primitiveService.getCategories(),
      ]);
      setPrimitives(primitivesData);
      setCategories(categoriesData);
    } catch (err: any) {
      console.error('Failed to load primitives:', err);
      setError(err.message || 'Failed to load primitives');
    } finally {
      setLoading(false);
    }
  };

  const filteredPrimitives = useMemo(() => {
    const filtered = applyFilters(primitives, filters, updatedFilterColumnsConfig);
    const sorted = [...filtered].sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      if (sortColumn === 'name') {
        av = a.name?.toLowerCase() ?? '';
        bv = b.name?.toLowerCase() ?? '';
      } else if (sortColumn === 'category') {
        av = (a.category ?? '').toLowerCase();
        bv = (b.category ?? '').toLowerCase();
      } else if (sortColumn === 'version') {
        av = a.version ?? 0;
        bv = b.version ?? 0;
      }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [primitives, filters, updatedFilterColumnsConfig, sortColumn, sortDirection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto text-accent-blue animate-spin" />
          <p className="mt-4 text-sm text-muted-foreground">Loading primitives...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto text-status-error mb-3" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Failed to Load Primitives
          </h3>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button variant="default" onClick={loadData}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-4">
        <DataTableFilter
          columns={filterColumns}
          filters={filters}
          actions={actions}
          strategy={strategy}
        />
      </Card>

      <p className="text-sm text-muted-foreground">
        {filteredPrimitives.length} primitive{filteredPrimitives.length !== 1 && 's'} found
      </p>

      <Card padding="lg">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="w-10" />
                <TableHead
                  className="cursor-pointer select-none hover:bg-muted"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">
                    Name
                    <SortIcon active={sortColumn === 'name'} direction={sortDirection} />
                  </div>
                </TableHead>
                <TableHead
                  className="w-40 cursor-pointer select-none hover:bg-muted"
                  onClick={() => handleSort('category')}
                >
                  <div className="flex items-center gap-2">
                    Category
                    <SortIcon active={sortColumn === 'category'} direction={sortDirection} />
                  </div>
                </TableHead>
                <TableHead>Description</TableHead>
                <TableHead
                  className="w-24 cursor-pointer select-none hover:bg-muted"
                  onClick={() => handleSort('version')}
                >
                  <div className="flex items-center justify-end gap-2">
                    Version
                    <SortIcon active={sortColumn === 'version'} direction={sortDirection} />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPrimitives.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={5} className="h-32 text-center">
                    <Inbox className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-sm font-medium text-foreground">No primitives found</p>
                    <p className="text-xs text-muted-foreground">
                      {filters.length > 0
                        ? 'Try adjusting your filters'
                        : 'No published primitives available yet'}
                    </p>
                  </TableCell>
                </TableRow>
              ) : filteredPrimitives.map((primitive) => {
                const isOpen = expandedId === primitive.id;
                return (
                  <React.Fragment key={primitive.id}>
                    <TableRow
                      className="cursor-pointer"
                      onClick={() => setExpandedId(isOpen ? null : primitive.id)}
                    >
                      <TableCell>
                        <ChevronRight
                          className={`w-4 h-4 text-muted-foreground transition-transform ${
                            isOpen ? 'rotate-90' : ''
                          }`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm font-medium text-foreground">
                        {primitive.name}
                      </TableCell>
                      <TableCell>
                        {primitive.category && (
                          <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-accent-blue/10 text-accent-blue">
                            {primitive.category}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground truncate max-w-md">
                        {typeof primitive.description === 'string' ? primitive.description : ''}
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground font-mono">
                        v{primitive.version}
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableCell colSpan={5} className="p-6">
                          <div className="space-y-4">
                            {typeof primitive.description === 'string' && primitive.description && (
                              <p className="text-sm text-foreground">
                                {primitive.description}
                              </p>
                            )}

                            <SchemaPreview
                              schema={primitive.inputSchema}
                              title="Input Schema"
                              emptyMessage="No inputs required"
                            />
                            <SchemaPreview
                              schema={primitive.outputSchema}
                              title="Output Schema"
                              emptyMessage="No outputs returned"
                            />

                            <div className="grid grid-cols-3 gap-4 pt-3 border-t border-border">
                              <div>
                                <dt className="text-xs uppercase tracking-wide text-muted-foreground/70 mb-1">
                                  Status
                                </dt>
                                <dd className="text-sm font-medium">
                                  {primitive.isPublished ? (
                                    <span className="text-status-success">Published</span>
                                  ) : (
                                    <span className="text-status-warning">Draft</span>
                                  )}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-xs uppercase tracking-wide text-muted-foreground/70 mb-1">
                                  Created
                                </dt>
                                <dd className="text-sm text-foreground font-medium">
                                  {fmt.formatDate(primitive.createdAt)}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-xs uppercase tracking-wide text-muted-foreground/70 mb-1">
                                  Updated
                                </dt>
                                <dd className="text-sm text-foreground font-medium">
                                  {fmt.formatDate(primitive.updatedAt)}
                                </dd>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default PrimitiveLibrary;
